import { resourcePatternToNodeKey } from "@p0security/shared/assessment/issues/aws";
import { grantNodeToPrivilegeSet } from "@p0security/shared/assessment/render";
import {
  ALL_SCOPE_SENTINEL,
  AssessmentScopeIntegrations,
  providerToScope,
  toKey,
  toScope,
} from "@p0security/shared/types/assessment";
import { GrantNode } from "@p0security/shared/types/assessment/data/index";
import { isa } from "@p0security/shared/types/is";
import { Descriptions, List } from "antd";
import { ScopeContext } from "components/Assessment/contexts/ScopeContext";
import { useContext } from "react";

import { ConditionDisplay } from "../../cells/Condition";
import { IdentityExternalLink } from "../../cells/Identity";
import { NodeLink } from "../../cells/NodeLink";
import {
  RiskGroupedPrivilegeList,
  RiskList,
} from "../../cells/PrivilegeAggregate";
import { Resource } from "../../cells/Resource";
import { NodeDescriptions } from "../NodeDescriptions";
import { DisplayList } from "../shared";
import { GrantAccessors } from "./GrantAccessors";
import { providerTitleElements } from "./shared";

const { Item } = Descriptions;

export const GrantNodeDisplay: React.FC<{
  node: GrantNode;
}> = ({ node }) => {
  const { scopeKey } = useContext(ScopeContext);
  const { data } = node;
  const { provider } = data;
  const integration = providerToScope(provider);

  let grantScope: string | undefined;
  // toKey throws an error if the data does not parse as a scope
  try {
    grantScope =
      isa(AssessmentScopeIntegrations, integration) && data.parent
        ? toKey({ type: "project", id: data.parent, integration })
        : undefined;
  } catch (e) {
    console.error("Error parsing grant scope", e);
  }
  return (
    <>
      <NodeDescriptions>
        <Item label="Principal">
          <IdentityExternalLink
            data={{
              ...node.data,
              type: node.data.principalType,
              label: node.data.principal,
            }}
            id={node.data.principal}
          />
        </Item>
        <Item
          label={
            providerTitleElements[node.data.provider ?? ALL_SCOPE_SENTINEL][
              "privilegeSet"
            ]
          }
        >
          {grantNodeToPrivilegeSet(node)}
        </Item>
        <Item label="Resources">
          <DisplayList>
            {node.data.resources.map((r, ix) => {
              let nodeKey = r;
              if (grantScope) {
                const scope = toScope(grantScope);
                if (provider === "aws" && scope !== ALL_SCOPE_SENTINEL) {
                  nodeKey = resourcePatternToNodeKey(node.key, scope.id);
                }
              }
              return (
                <List.Item key={ix}>
                  <NodeLink
                    node={{ key: nodeKey, type: "resource" }}
                    // resources are not visible in All Scopes, so we need to
                    // override the scope to the specific scope the grant
                    // belongs to
                    scopeOverride={grantScope}
                  />
                  &nbsp;
                  <Resource resource={node} index={ix} />
                </List.Item>
              );
            })}
          </DisplayList>
        </Item>
        {node.data.condition && typeof node.data.condition === "object" && (
          <Item label="Condition">
            <ConditionDisplay condition={node.data.condition} />
          </Item>
        )}

        <Item label="Risks">
          {scopeKey === ALL_SCOPE_SENTINEL ? (
            <RiskList risks={node.aggregates.risks} />
          ) : (
            <RiskGroupedPrivilegeList
              privileges={node.aggregates.privileges}
              provider={node.data.provider}
              showControl
            />
          )}
        </Item>

        <Item label="Accessible by">
          <GrantAccessors node={node} />
        </Item>
      </NodeDescriptions>
    </>
  );
};
